import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useAuth } from '../auth/AuthContext';
import { formatDate } from '../utils/formatDate';
import { exportCSV } from '../utils/exportCSV';
import { CALL_STATUS } from '../utils/constants';

export default function Students() {
  const { user } = useAuth();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [todaysConnected, setTodaysConnected] = useState(0);
  const [callsThisWeek, setCallsThisWeek] = useState(0);
  const [filters, setFilters] = useState({
    course: '',
    campus: '',
    hostel: '',
    assigned: '',
  });
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 25;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();


  useEffect(() => {
    let active = true;
    if (!user) return undefined;

    const fetchStudents = async () => {
      setLoading(true);
      setError('');

      const { data, error: fetchError } = await supabase
        .from('students')
        .select('id,name,phone,unique_id,whatsapp,parent_number,course,campus,hostel,mentor_id,created_at, mentor:mentors (full_name,email)')
        .order('created_at', { ascending: false });

      if (!active) return;

      if (fetchError) {
        console.error('Error fetching students', fetchError);
        setError('Unable to load students. Please try again.');
        setStudents([]);
      } else {
        setStudents(data ?? []);
      }
      setLoading(false);
    };

    fetchStudents();

    const channel = supabase
      .channel('public:students-list')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'students' }, fetchStudents)
      .subscribe();

    return () => {
      active = false;
      if (channel) supabase.removeChannel(channel);
    };
  }, [user]);

  useEffect(() => {
    const mentorFilter = searchParams.get('mentor');
    if (mentorFilter) {
      fetchMentorStats(mentorFilter);
    } else {
      setTodaysConnected(0);
      setCallsThisWeek(0);
    }
  }, [searchParams]);

  const fetchMentorStats = async (mentorId) => {
    const today = new Date().toISOString().split('T')[0];
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);

    const [todayRes, weekRes] = await Promise.all([
      supabase.from('calls').select('*', { count: 'exact', head: true })
        .eq('mentor_id', mentorId)
        .eq('call_status', CALL_STATUS.CONNECTED)
        .gte('created_at', `${today}T00:00:00.000Z`)
        .lt('created_at', `${today}T23:59:59.999Z`),
      supabase.from('calls').select('*', { count: 'exact', head: true })
        .eq('mentor_id', mentorId)
        .gte('created_at', weekAgo.toISOString()),
    ]);

    if (!todayRes.error) setTodaysConnected(todayRes.count || 0);
    if (!weekRes.error) setCallsThisWeek(weekRes.count || 0);
  };

  const filteredStudents = useMemo(() => {
    const term = search.trim().toLowerCase();
    const mentorFilter = searchParams.get('mentor');
    let filtered = students;

    if (mentorFilter) {
      filtered = filtered.filter((student) => student.mentor_id === mentorFilter);
    }

    if (term) {
      filtered = filtered.filter((student) => {
        return (
          student.name.toLowerCase().includes(term) ||
          student.unique_id.toLowerCase().includes(term) ||
          (student.phone || '').toLowerCase().includes(term) ||
          (student.whatsapp || '').toLowerCase().includes(term) ||
          (student.parent_number || '').toLowerCase().includes(term) ||
          (student.course || '').toLowerCase().includes(term) ||
          (student.campus || '').toLowerCase().includes(term) ||
          (student.hostel || '').toLowerCase().includes(term) ||
          (student.mentor?.email || '').toLowerCase().includes(term)
        );
      });
    }

    if (filters.course) filtered = filtered.filter(student => student.course === filters.course);
    if (filters.campus) filtered = filtered.filter(student => student.campus === filters.campus);
    if (filters.hostel) filtered = filtered.filter(student => student.hostel === filters.hostel);
    if (filters.assigned === 'assigned') filtered = filtered.filter(student => student.mentor_id);
    else if (filters.assigned === 'unassigned') filtered = filtered.filter(student => !student.mentor_id);

    return filtered;
  }, [students, search, searchParams, filters]);

  const filterOptions = useMemo(() => {
    const options = { course: [], campus: [], hostel: [] };
    students.forEach(student => {
      if (student.course && !options.course.includes(student.course)) options.course.push(student.course);
      if (student.campus && !options.campus.includes(student.campus)) options.campus.push(student.campus);
      if (student.hostel && !options.hostel.includes(student.hostel)) options.hostel.push(student.hostel);
    });
    options.course.sort();
    options.campus.sort();
    options.hostel.sort();
    return options;
  }, [students]);

  const activeFilters = useMemo(() => {
    const active = [];
    if (filters.course) active.push({ key: 'course', label: `Course: ${filters.course}`, value: filters.course });
    if (filters.campus) active.push({ key: 'campus', label: `Campus: ${filters.campus}`, value: filters.campus });
    if (filters.hostel) active.push({ key: 'hostel', label: `Hostel: ${filters.hostel}`, value: filters.hostel });
    if (filters.assigned) active.push({ key: 'assigned', label: filters.assigned === 'assigned' ? 'Assigned' : 'Unassigned', value: filters.assigned });
    return active;
  }, [filters]);

  // Reset to first page when filters or search change
  useEffect(() => {
    setPage(0);
  }, [search, filters, searchParams]);

  const removeFilter = (key) => {
    setFilters(prev => ({ ...prev, [key]: '' }));
  };

  const clearAllFilters = () => {
    setFilters({ course: '', campus: '', hostel: '', assigned: '' });
    setSearch('');
  };

  const handleExport = () => {
    const headers = ['Name', 'EPS ID', 'Student Number', 'Whatsapp', 'Parent Number', 'Course', 'Campus', 'Hostel', 'Mentor', 'Added'];
    const rows = filteredStudents.map(student => [
      student.name,
      student.unique_id,
      student.phone || '',
      student.whatsapp || '',
      student.parent_number || '',
      student.course || '',
      student.campus || '',
      student.hostel || '',
      student.mentor_id ? (student.mentor?.full_name || student.mentor?.email || 'Assigned') : 'Unassigned',
      formatDate(student.created_at)
    ]);
    exportCSV(headers, rows, 'students');
  };

  const mentorFilter = searchParams.get('mentor');
  const mentorName = useMemo(() => {
    if (!mentorFilter) return null;
    const studentWithMentor = students.find((s) => s.mentor_id === mentorFilter);
    return studentWithMentor?.mentor?.full_name || studentWithMentor?.mentor?.email || 'Unknown Mentor';
  }, [students, mentorFilter]);

  const totalFiltered = filteredStudents.length;
  const totalPages = Math.ceil(totalFiltered / PAGE_SIZE);
  const paginatedStudents = useMemo(() => {
    const start = page * PAGE_SIZE;
    return filteredStudents.slice(start, start + PAGE_SIZE);
  }, [filteredStudents, page]);

  const stats = useMemo(() => {
    const total = students.length;
    const assigned = students.filter(s => s.mentor_id).length;
    return { total, assigned, unassigned: total - assigned };
  }, [students]);

  return (
    <div className="space-y-8">
      <header className="space-y-2 animate-fade-in">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900 dark:text-white">
              {mentorName ? `Students of ${mentorName}` : 'Students'}
            </h1>
            <p className="text-slate-600 dark:text-slate-400 mt-1">
              {mentorName ? 'View students assigned to this mentor.' : 'View the full roster of students and their mentor assignments.'}
            </p>
          </div>
          <button onClick={handleExport} className="btn btn-secondary">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Export CSV
          </button>
        </div>
      </header>

      {!mentorFilter && (
        <section className="grid gap-4 sm:grid-cols-3 animate-fade-in">
          <div className="card p-4">
            <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">Total Students</p>
            <p className="text-2xl font-semibold text-slate-900 dark:text-white mt-1">{stats.total}</p>
          </div>
          <div className="card p-4">
            <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">Assigned</p>
            <p className="text-2xl font-semibold text-brand mt-1">{stats.assigned}</p>
          </div>
          <div className="card p-4">
            <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">Unassigned</p>
            <p className="text-2xl font-semibold text-amber-600 dark:text-amber-400 mt-1">{stats.unassigned}</p>
          </div>
        </section>
      )}

      {mentorFilter && (
        <section className="grid gap-4 sm:grid-cols-3 animate-fade-in">
          <div className="card p-4">
            <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">My Students</p>
            <p className="text-2xl font-semibold text-brand mt-1">{filteredStudents.length}</p>
          </div>
          <div className="card p-4">
            <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">Calls This Week</p>
            <p className="text-2xl font-semibold text-slate-900 dark:text-white mt-1">{callsThisWeek}</p>
          </div>
          <div className="card p-4">
            <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">Today's Calls</p>
            <div className="flex items-center gap-2 mt-1">
              <p className="text-2xl font-semibold text-slate-900 dark:text-white">{todaysConnected}</p>
              <span className="text-sm text-slate-500">/ 10</span>
            </div>
            <div className="w-full h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden mt-2">
              <div className="h-full bg-brand rounded-full transition-all" style={{ width: `${Math.min(todaysConnected * 10, 100)}%` }}></div>
            </div>
          </div>
        </section>
      )}

      <section className="card animate-fade-in">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <label className="relative flex-1 max-w-md">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">🔍</span>
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search by name, EPS ID, phone, course, campus, hostel..."
                className="input pl-10"
              />
            </label>
            <div className="flex flex-wrap items-center gap-2">
              <select
                value={filters.course}
                onChange={(e) => setFilters(prev => ({ ...prev, course: e.target.value }))}
                className="input select select-inline"
              >
                <option value="">All Courses</option>
                {filterOptions.course.map(course => <option key={course} value={course}>{course}</option>)}
              </select>

              <select
                value={filters.campus}
                onChange={(e) => setFilters(prev => ({ ...prev, campus: e.target.value }))}
                className="input select select-inline"
              >
                <option value="">All Campuses</option>
                {filterOptions.campus.map(campus => <option key={campus} value={campus}>{campus}</option>)}
              </select>

              <select
                value={filters.hostel}
                onChange={(e) => setFilters(prev => ({ ...prev, hostel: e.target.value }))}
                className="input select select-inline"
              >
                <option value="">All Hostels</option>
                {filterOptions.hostel.map(hostel => <option key={hostel} value={hostel}>{hostel}</option>)}
              </select>

              <select
                value={filters.assigned}
                onChange={(e) => setFilters(prev => ({ ...prev, assigned: e.target.value }))}
                className="input select select-inline"
              >
                <option value="">All Students</option>
                <option value="assigned">Assigned</option>
                <option value="unassigned">Unassigned</option>
              </select>
            </div>
          </div>

          {activeFilters.length > 0 && (
            <div className="filter-chips">
              {activeFilters.map(filter => (
                <span key={filter.key} className="chip active">
                  {filter.label}
                  <button onClick={() => removeFilter(filter.key)} className="chip-remove">×</button>
                </span>
              ))}
              <button onClick={clearAllFilters} className="text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300">
                Clear all
              </button>
            </div>
          )}
        </div>

        {error && (
          <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-400/40 dark:bg-rose-500/10 dark:text-rose-200">
            {error}
          </div>
        )}

        <div className="mt-6">
          {loading ? (
            <div className="flex h-40 items-center justify-center text-sm text-slate-500 dark:text-slate-400">Loading students…</div>
          ) : paginatedStudents.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">🎓</div>
              <h3 className="empty-state-title">No students found</h3>
              <p className="empty-state-description">
                {activeFilters.length > 0 || search
                  ? 'Try adjusting your filters or search terms.'
                  : 'No students have been added yet.'}
              </p>
              {(activeFilters.length > 0 || search) && (
                <button onClick={clearAllFilters} className="btn btn-secondary mt-4">Clear filters</button>
              )}
            </div>
          ) : (
            <>
              {/* Mobile: Card layout */}
              <div className="md:hidden space-y-3">
                {paginatedStudents.map((student) => (
                  <div
                    key={student.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate(`/students/${student.id}`)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/students/${student.id}`); } }}
                    className="card p-4 cursor-pointer hover:border-brand/30 active:scale-[0.99] transition-all"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-slate-900 dark:text-white truncate">{student.name}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{student.unique_id} · {student.phone || 'No phone'}</p>
                      </div>
                      {student.mentor_id ? (
                        <span className="text-xs font-medium text-brand bg-brand/8 px-2 py-0.5 rounded-full whitespace-nowrap flex-shrink-0">
                          {student.mentor?.full_name || 'Assigned'}
                        </span>
                      ) : (
                        <span className="text-xs font-medium text-amber-600 dark:text-amber-400 bg-amber-500/8 px-2 py-0.5 rounded-full flex-shrink-0">Unassigned</span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-slate-500 dark:text-slate-400">
                      {student.course && <span>{student.course}</span>}
                      {student.campus && <span>{student.campus}</span>}
                      {student.hostel && <span>{student.hostel}</span>}
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop: Table layout */}
              <div className="hidden md:block overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-950/50">
                <table className="min-w-full divide-y divide-slate-200 text-sm dark:divide-slate-800">
                  <thead className="bg-slate-100/60 dark:bg-slate-800/60 sticky top-0">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300">Name</th>
                      <th className="px-4 py-3 text-left font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300">EPS ID</th>
                      <th className="px-4 py-3 text-left font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300">Phone</th>
                      <th className="px-4 py-3 text-left font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300 hidden lg:table-cell">Whatsapp</th>
                      <th className="px-4 py-3 text-left font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300 hidden xl:table-cell">Parent</th>
                      <th className="px-4 py-3 text-left font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300">Course</th>
                      <th className="px-4 py-3 text-left font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300 hidden lg:table-cell">Campus</th>
                      <th className="px-4 py-3 text-left font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300 hidden xl:table-cell">Hostel</th>
                      <th className="px-4 py-3 text-left font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300">Mentor</th>
                      <th className="px-4 py-3 text-left font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300 hidden lg:table-cell">Added</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {paginatedStudents.map((student) => (
                      <tr
                        key={student.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => navigate(`/students/${student.id}`)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            navigate(`/students/${student.id}`);
                          }
                        }}
                        className="cursor-pointer hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/60 dark:hover:bg-slate-900/40"
                      >
                        <td className="px-4 py-3 font-medium text-slate-800 dark:text-slate-100">{student.name}</td>
                        <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{student.unique_id}</td>
                        <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{student.phone || '—'}</td>
                        <td className="px-4 py-3 text-slate-600 dark:text-slate-300 hidden lg:table-cell">{student.whatsapp || '—'}</td>
                        <td className="px-4 py-3 text-slate-600 dark:text-slate-300 hidden xl:table-cell">{student.parent_number || '—'}</td>
                        <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{student.course || '—'}</td>
                        <td className="px-4 py-3 text-slate-600 dark:text-slate-300 hidden lg:table-cell">{student.campus || '—'}</td>
                        <td className="px-4 py-3 text-slate-600 dark:text-slate-300 hidden xl:table-cell">{student.hostel || '—'}</td>
                        <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                          {student.mentor_id ? (
                            <span className="text-brand">{student.mentor?.full_name || student.mentor?.email || 'Assigned'}</span>
                          ) : (
                            <span className="text-amber-600 dark:text-amber-400">Unassigned</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-slate-500 dark:text-slate-400 hidden lg:table-cell">{formatDate(student.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        {totalFiltered > PAGE_SIZE && (
          <div className="mt-4 flex items-center justify-between">
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Showing {page * PAGE_SIZE + 1} to {Math.min((page + 1) * PAGE_SIZE, totalFiltered)} of {totalFiltered} students
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                className="btn btn-secondary"
              >
                Previous
              </button>
              <button
                onClick={() => setPage(p => p + 1)}
                disabled={page + 1 >= totalPages}
                className="btn btn-secondary"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}